import { Shield, Target, Lightbulb, TrendingUp, Users, FileCheck, Sparkles } from 'lucide-react';

export default function Features() {
  const features = [
    {
      icon: Shield,
      title: "Bias Reduction",
      description: "Removes unconscious bias by focusing solely on qualifications and experience"
    },
    {
      icon: Target,
      title: "Intelligent Screening",
      description: "AI-driven platform evaluates resumes and screens candidates with precision"
    },
    {
      icon: Lightbulb,
      title: "Adaptive Complexity",
      description: "Adjusts question difficulty in real-time based on candidate performance"
    },
    {
      icon: TrendingUp,
      title: "Data-Driven Insights",
      description: "Actionable analytics to understand your candidate pool better"
    },
    {
      icon: Users,
      title: "Personalized Experience",
      description: "Natural conversational AI that leaves a lasting positive impression"
    },
    {
      icon: FileCheck,
      title: "Automated Filtering",
      description: "Advanced algorithms match resumes against job descriptions automatically"
    }
  ];

  return (
    <div className="py-32 bg-transparent relative overflow-hidden">

      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500/10 rounded-full mb-6 border border-emerald-500/20">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">Intelligent Features</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Hire Right and Hire Faster
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            The future of recruitment is here. Intelligent automation meets human insight.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative p-8 rounded-3xl glass-dark transition-all duration-500 hover:scale-105 cursor-pointer border border-emerald-500/20 hover:border-emerald-500/50 shadow-lg hover:shadow-2xl hover:shadow-emerald-500/20"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 group-hover:from-emerald-500 group-hover:to-teal-500 rounded-2xl flex items-center justify-center mb-6 transition-all shadow-lg">
                  <feature.icon className="w-8 h-8 text-emerald-400 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-slate-400 group-hover:text-slate-300 transition-colors leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-3xl blur-3xl opacity-20"></div>
          <div className="relative glass-dark rounded-3xl p-12 md:p-16 text-center border border-emerald-500/30 shadow-2xl">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Seamless Integration with Your Workflow
            </h3>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Connect with your existing HR systems effortlessly. No disruption, just enhancement.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {['ATS Systems', 'HR Platforms', 'Email Tools', 'Calendar Apps'].map((integration, i) => (
                <div key={i} className="px-6 py-3 bg-white/5 rounded-xl text-white font-semibold border border-emerald-500/30 hover:border-emerald-500 transition-all hover:scale-105 cursor-pointer hover:bg-white/10">
                  {integration}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
